import React, { useState, useEffect, useRef } from 'react';
import styles from '../../styles/ls-request/LsRequestSidebar.module.scss';
import { connect } from 'react-redux';
import {
	createNewComment,
	updateComment,
	deleteComment,
} from '../../actions/lsRequestAction';
import { PencilFillIcon, TrashIcon, ArrowReturnLeftIcon } from '../../services/SvgLibrary';
import UserSelect from '../user/UserSelect';

const LsSidebarComments = (props) => {
	const { updateTargetLs, userProfile, allUserList } = props;
	const [commentsData, setCommentsData] = useState(updateTargetLs.comments || []);
	const [newComment, setNewComment] = useState('');
	const [editingComment, setEditingComment] = useState(null);
	const [editCommentText, setEditCommentText] = useState('');
	const [taggedUsers, setTaggedUsers] = useState([]); // Store mentions
	const [mentionQuery, setMentionQuery] = useState(''); // Search for mentions
	const [showMentionDropdown, setShowMentionDropdown] = useState(false); // Control dropdown visibility

	// Ref to keep the input focused
	const commentInputRef = useRef(null);

	useEffect(() => {
		// Update local commentsData whenever updateTargetLs.comments changes
		setCommentsData(updateTargetLs.comments || []);
	}, [updateTargetLs.comments]);

	const handleCreateComment = () => {
		if (!newComment.trim()) return;

		const commentData = {
			body: newComment,
			commenter: userProfile.full_name,
			ls_number: updateTargetLs.ls_number,
			mentions: taggedUsers,  // Include mentions in the payload
		};
		props.createNewComment(commentData);
		setNewComment('');
		setTaggedUsers([]);
	};

	const handleEditComment = (commentId, body) => {
		setEditingComment(commentId);
		setEditCommentText(body);
	};

	const handleUpdateComment = (commentId) => {
		if (!editCommentText.trim()) return;

		const updatedCommentData = {
			id: commentId,
			body: editCommentText,
		};
		props.updateComment(updatedCommentData);
		setEditingComment(null);
		setEditCommentText('');
	};

	const handleDeleteComment = (commentId) => {
		props.deleteComment(commentId);
	};

	const handleTagInput = (e) => {
		const value = e.target.value;
		const cursorPosition = e.target.selectionStart;

		// Update the comment input state
		setNewComment(value);

		// If "@" is not present, close the dropdown and clear mention query
		if (!value.includes('@')) {
			setMentionQuery('');
			setShowMentionDropdown(false);
			return;
		}

		// Extract the current word where the cursor is
		const precedingText = value.slice(0, cursorPosition);
		const mentionMatch = precedingText.match(/@(\S*)$/); // Match "@" followed by text

		if (mentionMatch) {
			const query = mentionMatch[1]; // Extract text after "@"

			// If no matching users, close dropdown but allow text input to continue
			const matchingUsers = allUserList.filter((user) =>
				user.full_name.toLowerCase().includes(query.toLowerCase())
			);

			if (matchingUsers.length === 0) {
				setMentionQuery('');
				setShowMentionDropdown(false);
			} else {
				setMentionQuery(query);
				setShowMentionDropdown(true);
			}
		} else {
			// If no mention match, clear mention query and close dropdown
			setMentionQuery('');
			setShowMentionDropdown(false);
		}
	};




	const handleSelectMention = (user) => {
		setNewComment((prev) => prev.replace(`@${mentionQuery}`, `@${user.full_name} `));
		setTaggedUsers((prev) => [...prev, user.full_name]);
		setMentionQuery('');
		setShowMentionDropdown(false);
		commentInputRef.current?.focus(); // Focus back to input field
	};

	return (
		<div className="container p-0">
			{/* Create new comment */}
			<div className="card mb-3">
				<div className="card-body">
					<textarea
						type="text"
						className="form-control"
						placeholder="Add a new comment... Use @ to tag someone"
						value={newComment}
						onChange={handleTagInput}
						ref={commentInputRef}
						style={{ overflow: 'auto', resize: 'none' }}
					/>
					{/* Render UserSelect Component */}
					{showMentionDropdown && (
						<UserSelect
							search={mentionQuery}
							handleClick={(e) => {
								const selectedUser = allUserList.find(
									(user) => user.full_name === e.target.innerText
								);
								if (selectedUser) {
									handleSelectMention(selectedUser);
								}
							}}
							focused={true}
						/>
					)}
					<button
						className="btn btn-primary mt-2"
						onClick={handleCreateComment}>
						Submit Comment
					</button>
				</div>
			</div>

			<div className={styles['comment-list']}>
				{commentsData.map((comment) => {
					const commenterProfile = props.allUserList.find(
						(user) => {
							return user.full_name === comment.commenter
						})

					const isCommenter = props.userProfile.full_name === comment.commenter;
					const renderTaggedUsers = (body) => {
						let skipIndex = -1; // To skip processed words

						const words = body.split(' ');
						return words.map((word, index) => {
							if (index <= skipIndex) return null; // Skip already processed words

							if (word.startsWith('@')) {
								let combinedName = word.slice(1); // Start building the name without "@"
								let tempIndex = index; // Index pointer
								let matchedUser = null;

								// Start checking names, ensure at least two words
								while (tempIndex < words.length) { // Cover all possibilities
									combinedName = combinedName.trim();
									const tempNameParts = combinedName.split(' ');

									if (tempNameParts.length >= 2) {
										matchedUser = props.allUserList.find(
											(user) =>
												user.full_name.toLowerCase() === combinedName.toLowerCase());

										if (matchedUser) {
											skipIndex = tempIndex; // Skip all words used in the matched name
											return (
												<a
													key={index}
													href={`/staff/${matchedUser.id}`}
													className="text-primary"
													style={{ textDecoration: 'none' }}
												>
													{matchedUser.full_name}{' '}
												</a>
											);
										}
									}

									tempIndex += 1;
									combinedName += ` ${words[tempIndex] || ''}`; // Add the next word to the combined name
								}
							}

							// Return normal word
							return <span key={index}>{word} </span>;
						}).filter(Boolean); // Remove null values
					};

					return (
						<div key={comment.id} className="card p-3 mb-3">

							{/* Display Commenter's Profile Picture */}
							<div className="d-flex align-items-center mb-2">
								<img
									src={commenterProfile?.profile_pic_icon?.file || '/path/to/default-profile-pic.jpg'}
									alt={comment.commenter}
									className="rounded-circle me-2"
									style={{ width: '40px', height: '40px', objectFit: 'cover' }}
								/>
								<span className="fw-bold">{comment.commenter}</span>
							</div>

							{/* Display Comment Body */}
							{editingComment === comment.id ? (
								<div>
									<textarea
										className="form-control"
										value={editCommentText}
										onChange={(e) => setEditCommentText(e.target.value)}
									/>
									<button
										className="btn btn-success mt-2 me-2"
										onClick={() => handleUpdateComment(comment.id)}>
										Save
									</button>
									<button
										className="btn btn-light mt-2"
										onClick={() => setEditingComment(null)}>
										Cancel
									</button>
								</div>
							) : (
								<div>
									<br></br>
									<p>{renderTaggedUsers(comment.body)}</p>
									<small className="text-muted">
										Commented on{' '}
										{new Date(comment.created_at).toLocaleString()}
									</small>
									<br></br>
									{comment.edited_at &&
										<small className="text-muted">
											Edited on{' '}
											{new Date(comment.edited_at).toLocaleString()}
										</small>}

									{comment.mentions && (
										<small className="text-muted">
											Mentioned: {comment.mentions.map((mention) => mention.full_name).join(', ')}
										</small>
									)}

									{/* Show edit and delete buttons only if the logged-in user is the commenter */}
									{isCommenter && (
										<div className="d-flex gap-2 mt-2">
											<button
												className="btn btn-outline-secondary btn-sm"
												onClick={() => handleEditComment(comment.id, comment.body)}
											>
												<PencilFillIcon width="14" />
											</button>
											<button
												className="btn btn-outline-danger btn-sm"
												onClick={() => handleDeleteComment(comment.id)}
											>
												<TrashIcon width="14" />
											</button>
										</div>
									)}
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	userProfile: state.userReducer.userProfile,
	updateTargetLs: state.lsRequestsReducer.updateTargetLs,
	allUserList: state.userReducer.allUserList
});

const mapDispatchToProps = {
	createNewComment,
	updateComment,
	deleteComment,
};

export default connect(mapStateToProps, mapDispatchToProps)(LsSidebarComments);